import { z } from "zod/v3";

export const ControlEvidenceDimensions = [
  "completeness",
  "accuracy",
  "relevance",
  "timeliness",
  "traceability",
] as const;

const ControlEvidenceDimensionSchema = z.object({
  score: z
    .number()
    .min(0)
    .max(100)
    .nullable()
    .describe(
      "Score on a 0–100 scale (80 means 80/100, never 0.8); null if not assessed.",
    ),
  reasoning: z.string().max(1500),
  evidenceRefs: z.array(z.string()).max(20),
});

export const ControlEvidenceAssessmentSchema = z.object({
  summary: z.string().max(2500),
  dimensions: z.object({
    completeness: ControlEvidenceDimensionSchema,
    accuracy: ControlEvidenceDimensionSchema,
    relevance: ControlEvidenceDimensionSchema,
    timeliness: ControlEvidenceDimensionSchema,
    traceability: ControlEvidenceDimensionSchema,
  }),
  findings: z
    .array(
      z.object({
        dimension: z.enum(ControlEvidenceDimensions),
        detail: z.string().max(1500),
        evidenceRefs: z.array(z.string()).max(20),
      }),
    )
    .max(25),
  recommendations: z.array(z.string().max(1000)).max(15),
  limitations: z.array(z.string().max(1000)).max(20),
});
export type ControlEvidenceAssessmentDTO = z.infer<
  typeof ControlEvidenceAssessmentSchema
>;

export const RiskAssessmentRecordReviewDTOSchema = z.object({
  version: z.literal(1),
  snapshotId: z.string(),
  dataHash: z.string(),
  criteriaHash: z.string(),
  riskCount: z.number().int().nonnegative(),
  recordCount: z.number().int().nonnegative(),
  expectedParts: z.number().int().nonnegative(),
  reviewedParts: z.number().int().nonnegative(),
  failedParts: z.number().int().nonnegative(),
  reviewedAt: z.string(),
  counts: z.object({ supported: z.number(), gap: z.number(), uncertain: z.number(), not_applicable: z.number() }),
  records: z.array(z.object({
    id: z.string(),
    outcome: z.enum(['supported', 'gap', 'uncertain', 'not_applicable']),
    explanation: z.string().max(800),
  })),
  conclusions: z.array(z.object({
    recordIds: z.array(z.string()),
    summary: z.string().max(500),
  })),
});
export type RiskAssessmentRecordReviewDTO = z.infer<typeof RiskAssessmentRecordReviewDTOSchema>;

export interface EvidenceRequirementReviewDTO {
  id: string;
  requirement: string;
  criteria: Array<{ id: number; key: string; text: string }>;
  verdict: 'supported' | 'unmet' | 'conflicted' | 'insufficient' | 'not_applicable';
  reason: string;
  populationCheck: boolean;
  populationComplete: boolean;
  citations: Array<{ evidenceRef: string; start: number; end: number; quote: string }>;
}

export interface EvidenceReviewCoverageDTO {
  evidenceRef: string;
  contentHash: string | null;
  method: 'direct' | 'retrieval';
  /** Index coverage is not proof that every record was inspected or every requirement met. */
  indexedParts?: number;
  expectedParts: number;
  reviewedParts: number;
  failedParts: number;
  complete: boolean;
  /** UTF-16 ranges in the frozen source, not byte offsets. */
  unreadRanges: Array<{ start: number; end: number }>;
}

export interface ControlEvidenceReviewDTO {
  id: string;
  status: "QUEUED" | "RUNNING" | "COMPLETED" | "FAILED";
  modelId: string;
  createdAt: string;
  error: string | null;
  stale: boolean;
  period: { from: string | null; to: string | null };
  result:
    | (ControlEvidenceAssessmentDTO & {
        incomplete: boolean;
        testCount: number;
        evidenceCount: number;
        consultedEvidenceCount: number;
        evidenceCoverage?: EvidenceReviewCoverageDTO[];
        requirementReviews?: EvidenceRequirementReviewDTO[];
        riskAssessmentReviews?: Array<{ evidenceRef: string; report: RiskAssessmentRecordReviewDTO }>;
        citations: Array<{
          ref: string;
          title: string;
          testIds: string[];
          testCode?: string;
          testTitle?: string | null;
          from?: string | null;
          to?: string | null;
          capturedAt?: string | null;
        }>;
      })
    | null;
}

export interface ControlApprovalDTO {
  workflowVersion?: number;
  legacyAccepted?: boolean;
  legacyTestReviews?: Array<{
    testId: string;
    testCode: string;
    testTitle: string | null;
    submittedAt: string;
    submittedByName: string | null;
    reviewedAt: string | null;
    reviewedByName: string | null;
  }>;
  stage:
    | "LEGACY_ACCEPTED"
    | "PREPARE"
    | "NEEDS_APPROVAL"
    | "APPROVED"
    | "CHANGES_REQUESTED"
    | "NOT_APPLICABLE";
  stale: boolean;
  excludedRequirements?: number;
  totalRequirements?: number;
  scopeReasons?: string[];
  requiredTests: number;
  approvedTests: number;
  testsReady: boolean;
  ready: boolean;
  canSubmit: boolean;
  canReview: boolean;
  canReopen: boolean;
  approvalConcerns?: Array<{
    testId: string;
    code: string;
    title: string | null;
    hasNonPassingObservations: boolean;
    gapDays: number;
  }>;
  isDesignatedApprover: boolean;
  approverName: string | null;
  approverConfigured: boolean;
  history: Array<{
    id: string;
    status: string;
    requestedBy: string;
    createdAt: string;
    reviewedBy: string | null;
    reviewedAt: string | null;
    comments: string | null;
    revisionNumber: number;
    requestedByName: string | null;
    reviewedByName: string | null;
    reopenedByName: string | null;
    reopenedAt: string | null;
    reopenReason: string | null;
  }>;
}
export interface ControlReviewContextDTO {
  id: string;
  label: string;
  framework: string;
  from: string | null;
  to: string | null;
}

export interface ControlRequirementScopesDTO {
  programId: string | null;
  requirements: Array<{
    frameworkDefId: string;
    frameworkName: string;
    frameworkControlId: string;
    title: string;
    applicability: "APPLICABLE" | "NOT_APPLICABLE";
    reason: string | null;
    affectedControls: number;
  }>;
}

/** An overall score exists only when all five dimensions were assessed. */
export function controlEvidenceReviewScore(
  dimensions: ControlEvidenceAssessmentDTO["dimensions"],
): number | null {
  const scores = ControlEvidenceDimensions.map(
    (dimension) => dimensions[dimension].score,
  );
  return scores.every((score) => score !== null)
    ? Math.round(
        scores.reduce<number>((total, score) => total + (score ?? 0), 0) /
          scores.length,
      )
    : null;
}
